import { useState, useEffect, useRef, memo } from "react";
import { useNavigate } from "@remix-run/react";
import SaveScreenModal from "../components/saveScreenModal";
import axios from "axios";
import '../styles/app.css';
import { useDeviceDetect } from '../hooks/useDeviceDetect';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faSave, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import MainAuthModal from '../components/authModals/App';
import Button from "../components/Button";
import { useLocation } from "@remix-run/react";
import TextAreaFuncComp from "./TextAreaFuncComp";
import CustomPagination from "./CustomPagination";
import OneTimePriceModal from "../components/OneTimePriceModal";
import { captureException } from "~/utilities/sentry.utility";
import HeroSection from "../components/HeroSection";

interface ScreenerFilter {
    name: string;
    key: string;
    shortDescription: string;
}

interface ScreenerSectionProps {
    filterList: FilterItem[];
    isHomePage: boolean;
    placeholder: string;
    isAuthenticated: boolean;
    user: any;
    displayPricingModal: boolean;
    isBeta: boolean;
}

export interface FilterItem {
    name: string;
    key: string;
    isText: boolean;
    operatorSuggestions: string[];
    shortDescription: string;
    type: string;
    is_amount_filter: boolean;
    values: { name: string; value: string }[];
    filter_type?: string;
    example_text?: string;
    glossary_key?: string;
}

// Add interface for column definition
interface DataTableColumn {
    name: string;
    selector: (row: any) => any;
    sortable: boolean;
    sortFunction?: (a: any, b: any) => number;
    cell?: (row: any) => JSX.Element | string;
}

const ScreenerSection: React.FC<ScreenerSectionProps> = ({ filterList, isHomePage, placeholder, isAuthenticated, user, displayPricingModal, isBeta }) => {

    const navigate = useNavigate();
    const location = useLocation();

    const { isDesktop } = useDeviceDetect();
    const [isScreenerIdeasOpen, setScreenerIdeasOpen] = useState(false);
    const [isFiltersOpen, setFiltersOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<ScreenerFilter | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [activeSuggestion, setActiveSuggestion] = useState<number>(0);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [filteredSuggestions, setFilteredSuggestions] = useState<FilterItem[]>(filterList);
    const [currentStep, setCurrentStep] = useState(1);
    const [queryArr, setQueryArr] = useState<string[]>([]);
    const [displayArr, setDisplayArr] = useState<string[]>([]);
    const [queryOriginalArr, setQueryOriginalArr] = useState<string[]>([]);
    const [searchTerm, setsearchTerm] = useState('');
    const [activeObjArr, setActiveObjArr] = useState<any[]>([]);
    const [operatorArray, setOperatorArray] = useState<any[]>([]);
    const [valuesArray, setValuesArray] = useState<any[]>([]);
    const [textValue, setTextValue] = useState("");
    const [isDisplayPricingModal, setIsDisplayPricingModal] = useState(false);
    const [inputTextValue, setInputTextValue] = useState("");
    const [bigIntFilters, setBigIntFilters] = useState([
        "free_cash_flow_avg_4q",
        "free_cash_flow_avg_6q",
        "revenue_last_quarter",
        "net_income_last_quarter",
        "gross_profit_last_quarter",
        "revenue_ttm",
        "net_income_ttm",
        "gross_profit_ttm",
        "market_cap",
        "total_assets",
        "total_current_assets",
        "total_non_current_assets",
        "total_liabilities",
        "total_current_liabilities",
        "total_non_current_liabilities",
        "total_equity",
        "enterprise_value_ttm",
        "ebitda"
    ]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10; // Single source of truth for page size
    const [columnHeader, setColumnHeader] = useState<string[]>([]);
    const [totalPages, setTotalPages] = useState(0);
    const [headerObj, setHeaderObj] = useState<DataTableColumn[]>([]);
    const [columnBodyRows, setColumnBodyRows] = useState<string[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [filteredKeys, setFilteredKeys] = useState<string[]>([]);
    const [keyMap, setkeyMap] = useState<any>({});
    const [tickerListData, setTickerListData] = useState<any[]>([]);
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const [activeEducationElementId, setActiveEducationElementId] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

    const [tableDataHeader, setTableDataHeader] = useState<any[]>([]);
    const [tableDataBody, setTableDataBody] = useState<any[]>([]);
    const [totalCompaniesCount, setTotalCompaniesCount] = useState(0);
    const [sortColumn, setSortColumn] = useState<string>('market_cap'); // default sort column
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // default sort direction (descending)
    const [testBoxQueryArr, setTestBoxQueryArr] = useState<string[]>([]);

    let userType = 'gold';

    const headerDisplayNames = {
        revenue_growth_past_3_years_total: "3Y Rev Growth",
        net_income_ttm: "Net Income TTM",
        // ...add more as needed
    };



    useEffect(() => {
        if (location.search) {
            initialQuerySubmit();
        } else {
            setQueryArr([]);
            setQueryOriginalArr([]);
            setCurrentStep(0);
            setActiveObjArr([]);
            setActiveSuggestion(-1);
            setColumnHeader([]);
            setHeaderObj([]);
            setColumnBodyRows([]);
            setFilteredData([]);
            setFilteredKeys([]);
            setkeyMap({});
            setTickerListData([]);
            setShowSuggestions(false);
            setSortColumn("market_cap");
            setSortDirection("desc");
            setTableDataHeader([]);
            setTableDataBody([]);
            setTotalPages(0);
            setTotalCompaniesCount(0)
        }
    }, [location.search]);


    const companyScreenerApi = async (queryArr: string[], limit: number, offset: number, sort_by: string, sort_direction: string) => {
        try {
            setIsLoading(true);
            const response = await axios.post('/api/company-screener', {
                data: queryArr,
                limit: limit,
                offset: offset,
                sort_by: sort_by,
                sort_direction: sort_direction
            });

            let apiResponse = response.data.data;
            let apiResponseTotal = response.data.total;
            setTotalCompaniesCount(apiResponseTotal);
            generateTableData(apiResponse);
            setQueryArr(queryArr);
            inputRef.current?.blur();
        } catch (err) {
            console.error('Error in companyScreenerApi:', err);
            captureException(`Error in companyScreenerApi: ${err}`);
        } finally {
            setIsLoading(false);
        }
    }

    const initialQuerySubmit = () => {
        // query is revenue_growth_past_3_years_totaland>=and5%
        if (!isHomePage) {
            const urlParams = new URLSearchParams(window.location.search);
            const query = urlParams.get('query');

            let currentobjectArr: any[] = [];

            if (query) {
                const queryArr = query.split('and');

                let dataArr = queryArr;
                let newQueryArr: string[] = [];

                dataArr.forEach(item => {
                    const parts = item.split(' ').map(part => part.trim());
                    if (parts.length >= 3) {
                        newQueryArr.push(parts[0], parts[1], parts[2]);
                        currentobjectArr.push(searchFilterObject(parts[0]));
                    }
                });
                setQueryArr(newQueryArr);
                setQueryOriginalArr(newQueryArr);
                setCurrentStep(queryArr.length);
                setActiveObjArr(currentobjectArr);
                setActiveSuggestion(-1);
                // screener api call
                companyScreenerApi(newQueryArr, 10, 0, sortColumn, sortDirection);

            }
        }
    }

    const searchFilterObject = (searchKey: string): FilterItem | undefined => {

        let filterObject = filterList.find(filter => filter.key.toLowerCase() === searchKey.toLowerCase());

        if (filterObject) {
            return filterObject;
        }
        return filterList.find(filter => filter.name.toLowerCase() === searchKey.toLowerCase());
    };

    const resetCurrentPage = () => {
        setCurrentPage(1);
    }

    useEffect(() => {
        // displayArr =  ['industry = medical_distribution'] to display in the input box
        const result = [];
        for (let i = 0; i < queryArr.length; i += 3) {
            const block = queryArr.slice(i, i + 3).join(' ');
            result.push(block);
        }

        setDisplayArr(result);

    }, [queryArr]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Ignore clicks on the offcanvas dialog
            const isOffcanvasClick = (event.target as Element).closest('[role="dialog"]');
            if (isOffcanvasClick) return;

            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setActiveSuggestion(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (activeSuggestion >= 0 && suggestionsRef.current) {
            const suggestionsList = suggestionsRef.current.querySelector('.suggestions-list');
            const selectedElement = suggestionsList?.children[activeSuggestion] as HTMLElement;

            if (selectedElement && suggestionsList) {
                const containerHeight = suggestionsList.clientHeight;
                const scrollTop = suggestionsList.scrollTop;
                const elementTop = selectedElement.offsetTop;
                const elementHeight = selectedElement.offsetHeight;

                // Scroll down if element is below viewport
                if (elementTop + elementHeight > scrollTop + containerHeight) {
                    suggestionsList.scrollTop = elementTop + elementHeight - containerHeight;
                }
                // Scroll up if element is above viewport
                else if (elementTop < scrollTop) {
                    suggestionsList.scrollTop = elementTop;
                }
            }
        }
    }, [activeSuggestion]);

    const onViewExample = async (query: string) => {
        if (isHomePage) {

            navigate(`/screener?query=${query}`);
        } else {
            setTextValue('');
            let queryList = query.split(' ');
            let dataArr: string[] = [];
            const filtered = filterList.filter((item: any) =>
                item.key.toLowerCase() === queryList[0].toLowerCase()
            );

            if (filtered.length > 0 && filtered[0].type === 'int' || filtered.length > 0 && filtered[0].type === 'float') {
                setQueryOriginalArr(queryList);
                dataArr = queryList
            } else {
                let valueList = filtered[0]["values"];


                if (valueList.length > 0) {

                    let dataValue: any[] = []
                    valueList.forEach((item: any, index: number) => {
                        if (item.value.toLowerCase() === queryList[2].toLowerCase()) {
                            dataValue.push(item);
                        }
                    });
                    dataArr = [queryList[0], queryList[1], dataValue[0].value];
                    setQueryOriginalArr([queryList[0], queryList[1], dataValue[0].value]);
                }


            }
            setActiveObjArr(filtered);
            setCurrentStep(2);
            setQueryArr(queryList);
            setsearchTerm(query);

            // screener api call
            await companyScreenerApi(dataArr, 10, 0, "market_cap", "desc");
            setShowSuggestions(false);
            setFiltersOpen(false);
        }
    }

    const saveScreener = async () => {
        if (isAuthenticated) {
            setShowSaveFilterModal(true);
        } else {
            setIsSignInModalOpen(true);
        }
    };

    const handleCloseFilters = () => {
        setFiltersOpen(false);
        setActiveEducationElementId(null);
    }

    const handleScreenIdeasClick = (idea: any) => {
        let filters = idea.filters.join('and');
        const encodedQuery = encodeURIComponent(filters);

        navigate(`/screener?query=${encodedQuery}`, { replace: false, state: { needRefresh: true } });
    }

    const onQueryAppend = (filter: string[]) => {

        // screener api call
        companyScreenerApi(filter, 10, 0, sortColumn, sortDirection);

    }

    const handleFilterDataApi = (queryArr: string[]) => {
        companyScreenerApi(queryArr, 10, 0, sortColumn, sortDirection);
        inputRef.current && inputRef.current.blur();
    }

    const generateTableData = (apiResponse: any[]) => {
        if (!apiResponse.length) return { headers: [], data: [] };

        // Define priority fields that should appear first (replace ticker_symbol and ticker_name with ticker)
        const priorityFields = ['ticker', 'market_cap'];

        // Get all fields from the first row, excluding ticker_symbol and ticker_name
        const allFields = Object.keys(apiResponse[0])
            .filter(key => !['company_id', 'exchange', 'logo_url', 'ticker_id', 'ticker_symbol', 'ticker_name'].includes(key))
            .filter(key => !key.endsWith('_formatted'));

        // Sort fields: priority fields first, then others
        const sortedFields = [
            ...priorityFields.filter(field => field === 'ticker' || allFields.includes(field)),
            ...allFields.filter(field => !priorityFields.includes(field))
        ];

        // Generate headers (replace ticker with 'Ticker')
        const headers = [
            { field: 'ticker', name: 'Ticker', isLink: true, width: '230px' },
            ...sortedFields.filter(field => field !== 'ticker').map(field => ({
                field,
                name: headerDisplayNames[field as keyof typeof headerDisplayNames]
                    || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                hasFormatted: `${field}_formatted` in apiResponse[0],
                isLink: false,
                width:
                    field === 'market_cap' ? '140px' :
                        '120px'
            }))
        ];

        // Generate row data
        const data = apiResponse.map(row => {
            const tickerCell = {
                type: 'link',
                ticker_symbol: row.ticker_symbol,
                ticker_name: row.ticker_name,
                url: `/ticker/${row.ticker_id}/${row.exchange}/${row.ticker_symbol}?type=company&cid=${row.company_id}`
            };
            return [
                tickerCell,
                ...sortedFields.filter(field => field !== 'ticker').map(field => {
                    const formattedField = `${field}_formatted`;
                    let value = row[formattedField] !== undefined ? row[formattedField] : row[field];
                    if (typeof value === 'boolean') {
                        value = value ? 'Yes' : 'No';
                    }
                    return {
                        type: 'text',
                        value
                    };
                })
            ];
        });

        setTableDataHeader(headers);
        setTableDataBody(data);

        return { headers, data };
    };

    const onPaginationChange = async (page: number) => {
        if (!isAuthenticated) {
            setIsSignInModalOpen(true);
            return;
        }
        setCurrentPage(page); // Update the current page
        const queryToUse = queryArr.length > 0 ? queryArr : testBoxQueryArr;
        await companyScreenerApi(queryToUse, pageSize, (page - 1) * pageSize, sortColumn, sortDirection);
        inputRef.current?.blur();
    }

    const paginatedTotalPages = Math.ceil(totalCompaniesCount / pageSize);

    const handleHeaderClick = async (header: any) => {

        if (!isAuthenticated) {
            setIsSignInModalOpen(true);
            return;
        }

        // Skip if it's a link column
        if (header.isLink) return;

        // Toggle sort direction if clicking same column, otherwise set to desc
        const newDirection = sortColumn === header.field
            ? (sortDirection === 'asc' ? 'desc' : 'asc')
            : 'desc';

        setSortColumn(header.field);
        setSortDirection(newDirection);
        setCurrentPage(1);
        await companyScreenerApi(testBoxQueryArr, 10, 0, header.field, newDirection);
        inputRef.current?.blur();
    };

    const handleTickerClick = (url: string) => {
        return (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            // if (!isAuthenticated) {
            //     setIsSignInModalOpen(true);
            //     return;
            // }
            window.open(url, '_blank');
        }
    }

    return (
        <>
            <div className="text-center justify-center lg:max-w-5xl mx-auto">
                {/* Screener Ideas Section - Only show when no results */}
                {!isLoading && tableDataBody.length === 0 && (
                    <>
                    <HeroSection />
                        <div className="mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto px-5">
                                {[
                                    {
                                        title: "High revenue growth & profitable",
                                        icon: <FontAwesomeIcon icon={faLightbulb} className="h-4.5 w-4.5 text-yellow-500" />,
                                        filters: ["revenue_growth_past_3_years_cagr > 25%", "net_income_ttm > 1M"]
                                    },
                                    {
                                        title: "Founder Lead Companies",
                                        icon: <FontAwesomeIcon icon={faLightbulb} className="h-4.5 w-4.5 text-yellow-500" />,
                                        filters: ["founder_ceo_operator = true"]
                                    },
                                    {
                                        title: "Heavily shorted companies",
                                        icon: <FontAwesomeIcon icon={faLightbulb} className="h-4.5 w-4.5 text-yellow-500" />,
                                        filters: ["shares_short_float_percentage > 10%"],
                                        lgOnly: true,
                                    },
                                    {
                                        title: "Heavy insider buying stocks",
                                        icon: <FontAwesomeIcon icon={faLightbulb} className="h-4.5 w-4.5 text-yellow-500" />,
                                        filters: ["insider_trading_3_months_trend = BUY"]
                                    }
                                ].map((idea, index) => (
                                    <div
                                        onClick={() => handleScreenIdeasClick(idea)}
                                        key={index}
                                        className={`bg-background-dark flex items-center justify-between cursor-pointer p-2 py-2.5 md:py-3 text-left border-[1px] border-gray-200 rounded-xl hover:border-green-600 hover:bg-green-50 transition-all duration-200 hover:shadow-md animate-[wobbleAndHighlight_0.5s_ease-in-out] hover:animate-[wobbleAndHighlight_0.5s_ease-in-out] ${idea.lgOnly ? 'hidden lg:flex' : ''
                                            }`}
                                        style={{ animationDelay: `${1 + (index * 0.1)}s` }}
                                    >
                                        <span className="flex items-center gap-3">
                                            <span className="text-xl -mt-0.5">{idea.icon}</span>
                                            <span className="text-sm font-medium text-gray-900">{idea.title}</span>
                                        </span>
                                        <Button
                                            type="primary"
                                            size="xs"
                                            onClick={() => handleScreenIdeasClick(idea)}
                                            iconPosition="left"
                                        >
                                         Apply
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* OR Divider */}
                        <div className="max-w-3xl mx-auto px-4 my-6">
                            <div className="flex items-center justify-center">
                                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-gray-300"></div>
                                <span className="px-3 py-2 text-black font-semibold text-sm md:text-base tracking-wide uppercase bg-background-dark rounded-full mx-3">OR</span>
                                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-300 to-gray-300"></div>
                            </div>
                        </div>
                    </>
                )}

                {/* Text Area Section */}
                <div className="relative p-2 md:p-4 max-w-auto md:max-w-2xl lg:max-w-3xl mx-auto" ref={suggestionsRef}>
                    {
                        isDesktop ?
                            <TextAreaFuncComp filterList={filterList} isHomePage={isHomePage} handleFilterDataApi={handleFilterDataApi} onScreenerQueryAppend={onQueryAppend} isDesktop={isDesktop} isActive={true} isInputFocused={false} companyScreenerApi={companyScreenerApi} setTestBoxQueryArr={setTestBoxQueryArr} placeholder={placeholder} resetCurrentPage={resetCurrentPage} userId={user?.id} isAuthenticated={isAuthenticated} />
                            :
                            <TextAreaFuncComp filterList={filterList} isHomePage={isHomePage} handleFilterDataApi={handleFilterDataApi} onScreenerQueryAppend={onQueryAppend} isDesktop={isDesktop} isActive={false} isInputFocused={false} companyScreenerApi={companyScreenerApi} setTestBoxQueryArr={setTestBoxQueryArr} placeholder={placeholder} resetCurrentPage={resetCurrentPage} userId={user?.id} isAuthenticated={isAuthenticated} />
                    }
                </div>

                {
                    isLoading && (
                        <div className="flex justify-center items-center mt-8">
                            <div className="relative">
                                <div className="w-12 h-12 border-4 border-gray-200 border-t-green-600 rounded-full animate-spin"></div>
                            </div>
                        </div>
                    )
                }

                {
                    totalCompaniesCount > 0 && !isLoading &&
                    <div>
                        <div className="w-full grid grid-cols-1 md:grid-cols-3 items-center px-2 mt-5">
                            {/* Centered message */}
                            <div className="col-span-1 md:col-start-2 flex justify-center items-center text-gray-500 text-sm">
                                <span>Found</span>
                                <span className="mx-1 py-1 px-2 bg-background-dark text-black rounded-full font-semibold text-base">{totalCompaniesCount}</span>
                                <span>companies matching</span>
                            </div>
                            {/* Save button right on desktop, below on mobile */}
                            <div className="col-span-1 flex justify-center md:justify-end mt-2 md:mt-0">
                                <Button
                                    type="outline"
                                    size="small"
                                    onClick={saveScreener}
                                    icon={<FontAwesomeIcon icon={faSave} className="w-4 h-4" />}
                                    iconPosition="left"
                                    className="ml-2"
                                    aria-label="Save screener"
                                >
                                    <span className="md:inline">Save</span>
                                </Button>
                            </div>
                        </div>
                        {
                            totalCompaniesCount > 0 &&
                            (
                                <div className="flex-1 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 financials-table w-full mb-2.5 min-h-[450px] mt-3">
                                    <table className="min-w-full table-fixed border-spacing-0 border border-separate overflow-hidden rounded-xl">
                                        <thead className="bg-background-dark rounded-t-xl">
                                            <tr>
                                                {tableDataHeader.map((header: any, idx: number) => (
                                                    <th
                                                        key={idx}
                                                        style={{ width: header.width }}
                                                        className={`px-6 py-3 text-left text-sm font-medium text-black whitespace-nowrap ${idx === 0 ? 'rounded-tl-xl' : idx === tableDataHeader.length - 1 ? 'rounded-tr-xl' : ''
                                                            }`}
                                                    >
                                                        <div
                                                            className="flex items-center cursor-pointer"
                                                            onClick={() => handleHeaderClick(header)}
                                                        >
                                                            <span>{header.name}</span>
                                                            {!header.isLink && (
                                                                <div className="ml-2 flex flex-col items-center -space-y-2">
                                                                    <FontAwesomeIcon
                                                                        icon={faSortUp}
                                                                        className={`${sortColumn === header.field && sortDirection === 'asc' ? 'text-black' : 'text-gray-300'}`}
                                                                        style={{ fontSize: '13px' }}
                                                                    />
                                                                    <FontAwesomeIcon
                                                                        icon={faSortDown}
                                                                        className={`${sortColumn === header.field && sortDirection === 'desc' ? 'text-black' : 'text-gray-300'}`}
                                                                        style={{ fontSize: '13px' }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-gray-200">
                                            {tableDataBody.map((row: any, rowIndex: number) => (
                                                <tr key={rowIndex} className="transition-colors duration-200 group hover:bg-background-dark">
                                                    {row.map((cell: any, colIndex: number) => (
                                                        <td
                                                            key={colIndex}
                                                            style={{ width: tableDataHeader[colIndex]?.width }}
                                                            className="px-6 py-2.5 text-left whitespace-nowrap"
                                                        >
                                                            {cell.type === 'link' ? (
                                                                <a
                                                                    // href={cell.url}
                                                                    href="#"
                                                                    onClick={handleTickerClick(cell.url)}
                                                                    target="_blank"
                                                                    className="text-green-600 text-sm ticker-ellipsis"
                                                                    style={{ display: 'block' }}
                                                                >
                                                                    <span className="text-sm text-black">{cell.ticker_symbol}</span> - {cell.ticker_name}
                                                                </a>
                                                            ) : (
                                                                <div className="text-sm text-gray-900">{cell.value}</div>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>

                                    </table>

                                </div>
                            )
                        }
                        <CustomPagination
                            currentPage={currentPage}
                            totalPages={paginatedTotalPages}
                            onPageChange={onPaginationChange}
                        />
                    </div>
                }

                {
                    (totalCompaniesCount === 0 && queryArr.length > 0 && !isLoading) &&
                    (
                        <div className="flex justify-center items-center h-full mt-10">
                            <span className="text-gray-500 text-sm">No companies found</span>
                        </div>
                    )
                }

                {
                    showSaveFilterModal &&
                    <SaveScreenModal
                        isOpen={showSaveFilterModal}
                        onClose={setShowSaveFilterModal}
                        displayData={displayArr}
                        userId={user?.id}
                    />
                }
                {
                    isSignInModalOpen &&
                    <MainAuthModal
                        isOpen={isSignInModalOpen}
                        onClose={setIsSignInModalOpen}
                        query={displayArr.join('and')}
                    />

                }

                {/* TODO: Add back in when free trial is implemented */}
                {/* {
                    isDisplayPricingModal &&
                    <OneTimePriceModal
                        isOpen={isDisplayPricingModal}
                        onClose={setIsDisplayPricingModal}
                        userId={user?.id}
                    />
                } */}
            </div>
        </>
    );
};

export default memo(ScreenerSection);